import type { ActingAsProfile, Partner, Profile } from "@tilt/admin-sdk";

/**
 * All role-aware predicates in this module accept ``Profile`` OR
 * ``ActingAsProfile`` (the latter extends the former). Callers MUST
 * pass the acting-as profile - the response of
 * ``GET /admin/v1/profile/acting-as`` - so that UI gates swap
 * perspective during impersonation. Pointing a gate at plain
 * ``/profile`` is a bug: the actor's roles will leak into the view
 * of whoever they're impersonating.
 */
export type RoleProfile = ActingAsProfile | Profile;

/**
 * Role / permission helpers shared by the sidebar + Partners +
 * PartnerDetail pages. Mirrors the backend gates in
 * tilt-admin-api/src/tilt_admin_api/handlers/partners.py so the UI
 * doesn't surface actions the server will 403 on.
 *
 * These are pure predicates - no network, no hooks - so they're cheap
 * to re-evaluate in render paths and trivially unit-testable.
 *
 * Caveat: these mirror the backend at best-effort parity. The server
 * is still the source of truth (audit-attributable 403s), so a UI
 * miss won't grant unauthorized access - it just means a button the
 * server would accept gets hidden, or a button the server would
 * reject gets shown + 403s on click. We aim to match the server rule
 * exactly for the common (partner_admin + scope) case; exotic edge
 * cases (role changes mid-session, etc.) still rely on the API.
 */

/**
 * Mirror of ``_ROLE_SCOPE_COMPAT`` in
 * tilt-admin-api/src/tilt_admin_api/handlers/user_memberships.py -
 * which roles are meaningful at which scope tier. Offered as a helper
 * so the AddMembershipForm can gate the role dropdown per chosen
 * scope instead of letting the backend 400 on an invalid combo.
 *
 * ``super_admin`` is further constrained on the backend to the
 * platform partner row specifically; the UI gates it by picker (only
 * show platform in the partner selector when the caller picks that
 * role).
 */
export const ROLE_SCOPE_COMPAT: Record<
  "super_admin" | "partner_admin" | "partner_viewer" | "organization_admin" | "location_manager",
  ReadonlyArray<"partner" | "organization" | "location">
> = {
  super_admin: ["partner"],
  partner_admin: ["partner"],
  partner_viewer: ["partner"],
  organization_admin: ["organization"],
  location_manager: ["location"],
};

/**
 * Reverse lookup: roles compatible with a given scope_kind. Used by
 * the UI to show only valid role options once a scope is picked.
 */
export function rolesForScope(
  scopeKind: "partner" | "organization" | "location",
): ReadonlyArray<keyof typeof ROLE_SCOPE_COMPAT> {
  const roles = Object.keys(ROLE_SCOPE_COMPAT) as Array<keyof typeof ROLE_SCOPE_COMPAT>;
  return roles.filter((r) => ROLE_SCOPE_COMPAT[r].includes(scopeKind));
}

/** Roles that can mutate PARTNER-tier rows. Narrow set - a partner-
 *  tier operation (edit partner brand, flip a partner flag, delete
 *  a partner row) takes ``partner_admin`` or ``super_admin``; an
 *  organization_admin / location_manager does NOT qualify here (they
 *  can't reach up to the partner row per the 2026-04-24 matrix). */
const PARTNER_WRITE_ROLES = new Set(["super_admin", "partner_admin"]);

/** Roles that can mutate at any TIER they hold a grant on. Keeps the
 *  check-by-grant loops below readable. */
const ANY_WRITE_ROLES = new Set([
  "super_admin",
  "partner_admin",
  "organization_admin",
  "location_manager",
]);

/** Back-compat alias for existing callers that reference ``WRITE_ROLES``
 *  expecting partner-tier semantics. */
const WRITE_ROLES = PARTNER_WRITE_ROLES;

/** True when the profile carries any super_admin grant. */
export function isSuperAdmin(profile: RoleProfile | undefined): boolean {
  return !!profile?.roles.some((r) => r.role === "super_admin");
}

/**
 * ltree-containment helper: does ``grantPath`` cover ``candidatePath``?
 * A grant at ``platform.finterra`` covers ``platform.finterra.acme``
 * but NOT ``platform.other``. Matches the Postgres ``path <@`` operator
 * the backend uses - we simulate it as a string prefix check with a
 * trailing dot guard so ``platform.fin`` doesn't spuriously match
 * ``platform.finterra``.
 */
export function pathCovers(grantPath: string, candidatePath: string): boolean {
  if (grantPath === candidatePath) return true;
  return candidatePath.startsWith(`${grantPath}.`);
}

/**
 * PARTNER-tier write predicate - mirrors
 * ``caller.require_write_partner`` on the server. Super_admin covers
 * everything; partner_admin covers anything beneath any of their
 * partner-scope grants. Org-scope / loc-scope grants do NOT qualify
 * here (they can't reach up to the partner row).
 */
export function hasWriteOn(profile: RoleProfile | undefined, candidatePath: string): boolean {
  if (!profile) return false;
  return profile.roles.some(
    (r) =>
      PARTNER_WRITE_ROLES.has(r.role) &&
      (r.scope_kind ?? "partner") === "partner" &&
      pathCovers(r.partner_path, candidatePath),
  );
}

/**
 * ORG-tier write predicate - mirrors
 * ``caller.require_write_org(org_id, partner_path)``. Three grants
 * qualify:
 *   1. Any partner-tier write grant whose partner_path covers
 *      ``orgPartnerPath`` (partner_admin managing a child org).
 *   2. A partner-scope super_admin (covers everything).
 *   3. An org-scope grant that pins ``organization_id === orgId``
 *      with role in ``ANY_WRITE_ROLES`` (organization_admin on that
 *      specific org).
 */
export function hasWriteOnOrg(
  profile: RoleProfile | undefined,
  orgId: string,
  orgPartnerPath: string,
): boolean {
  if (!profile) return false;
  if (isSuperAdmin(profile)) return true;
  return profile.roles.some((r) => {
    if (!ANY_WRITE_ROLES.has(r.role)) return false;
    const kind = r.scope_kind ?? "partner";
    if (kind === "partner" && PARTNER_WRITE_ROLES.has(r.role)) {
      return pathCovers(r.partner_path, orgPartnerPath);
    }
    if (kind === "organization") {
      return r.organization_id === orgId;
    }
    return false;
  });
}

/**
 * LOCATION-tier write predicate - mirrors
 * ``caller.require_write_location(location_id, organization_id, partner_path)``.
 * Four grants qualify: partner-scope (covers descendants),
 * super_admin, org-scope matching ``orgId``, or loc-scope matching
 * ``locationId``.
 */
export function hasWriteOnLocation(
  profile: RoleProfile | undefined,
  locationId: string,
  orgId: string,
  orgPartnerPath: string,
): boolean {
  if (!profile) return false;
  if (isSuperAdmin(profile)) return true;
  return profile.roles.some((r) => {
    if (!ANY_WRITE_ROLES.has(r.role)) return false;
    const kind = r.scope_kind ?? "partner";
    if (kind === "partner" && PARTNER_WRITE_ROLES.has(r.role)) {
      return pathCovers(r.partner_path, orgPartnerPath);
    }
    if (kind === "organization") {
      return r.organization_id === orgId;
    }
    if (kind === "location") {
      return r.location_id === locationId;
    }
    return false;
  });
}

/**
 * Sidebar gate: caller holds at least one grant that covers the
 * PARTNER tier (partner_admin or partner_viewer or super). An
 * org_admin / location_manager does not pass; they shouldn't see the
 * Partners nav at all per the 2026-04-24 matrix (no cross-tier
 * visibility upward).
 */
export function hasAnyPartnerTierGrant(profile: RoleProfile | undefined): boolean {
  if (!profile) return false;
  if (isSuperAdmin(profile)) return true;
  return profile.roles.some((r) => (r.scope_kind ?? "partner") === "partner");
}

/** Sidebar gate for the Organizations nav. True if the caller has a
 *  partner-tier grant (sees all orgs in subtree) OR an org-scope grant
 *  (sees their org). Loc-scope grants don't qualify - they'd see
 *  nothing useful there. */
export function hasAnyOrgTierGrant(profile: RoleProfile | undefined): boolean {
  if (!profile) return false;
  if (isSuperAdmin(profile)) return true;
  return profile.roles.some(
    (r) => (r.scope_kind ?? "partner") === "partner" || r.scope_kind === "organization",
  );
}

/** Sidebar gate for the Locations nav. True if the caller has any
 *  partner / org / location grant. */
export function hasAnyLocationTierGrant(profile: RoleProfile | undefined): boolean {
  if (!profile) return false;
  if (isSuperAdmin(profile)) return true;
  return profile.roles.length > 0;
}

/**
 * True when the caller can create a sub-partner beneath ``parent``.
 *
 * Matches the two-part backend rule in partners._create_partner:
 *   1. ``parent.can_manage_sub_partners === true`` (or caller is
 *      super_admin, which bypasses the flag).
 *   2. Caller has write scope on ``parent.path``.
 *
 * Both must hold. Before the fix in PR #72 we only checked (1), which
 * surfaced the CTA to ``partner_viewer`` and other read-only roles
 * who would always 403 on click.
 */
export function canCreateSubPartnerUnder(
  profile: RoleProfile | undefined,
  parent: Pick<Partner, "can_manage_sub_partners" | "path"> | null | undefined,
): boolean {
  if (isSuperAdmin(profile)) return true;
  if (!parent?.can_manage_sub_partners) return false;
  return hasWriteOn(profile, parent.path);
}

/**
 * True when the caller can create *any* sub-partner somewhere in
 * ``visiblePartners`` - used to gate the Partners list page's
 * "New partner" button.
 *
 * Intentionally a reduction over ``canCreateSubPartnerUnder`` so the
 * scope + flag + write-grant rules stay in lockstep with the single-
 * parent version. Caller should pass the widest reasonable candidate
 * list (not just the scoped view) so a caller who has a grant on a
 * partner outside the current UI scope still sees the CTA.
 */
export function canCreateAnySubPartner(
  profile: RoleProfile | undefined,
  visiblePartners: ReadonlyArray<Pick<Partner, "can_manage_sub_partners" | "path">>,
): boolean {
  if (isSuperAdmin(profile)) return true;
  return visiblePartners.some((p) => canCreateSubPartnerUnder(profile, p));
}

/**
 * Revised role matrix (2026-04-24): a partner_admin may delete only
 * DIRECT sub-partners - children whose parent row sits inside one of
 * the caller's write grants. Super_admin bypasses. Platform row is
 * un-deletable (caller should also filter partner_type === 'platform'
 * in the UI, but backend 400s if they try).
 */
export function canDeletePartner(
  profile: RoleProfile | undefined,
  target: Pick<Partner, "partner_type" | "path">,
  parent: Pick<Partner, "path"> | null | undefined,
): boolean {
  if (target.partner_type === "platform") return false;
  if (isSuperAdmin(profile)) return true;
  if (!parent) return false;
  return hasWriteOn(profile, parent.path);
}

/**
 * Revised role matrix (2026-04-24): impersonation is no longer
 * super-only. A partner_admin may impersonate a user when:
 *   1. The target's primary_partner has a parent partner P, AND
 *   2. P.can_impersonate === true, AND
 *   3. The caller has partner-tier write on P.path.
 *
 * Super_admin bypasses all three checks.
 *
 * The parent partner + its flag come from the partners list (the
 * Impersonate page preloads both the target user and every partner
 * the caller can see, then threads the parent row into this helper).
 */
export function canImpersonateTarget(
  profile: RoleProfile | undefined,
  targetParentPartner: Pick<Partner, "path" | "can_impersonate"> | null | undefined,
): boolean {
  if (isSuperAdmin(profile)) return true;
  if (!targetParentPartner?.can_impersonate) return false;
  return hasWriteOn(profile, targetParentPartner.path);
}

/**
 * True when the caller can start ANY impersonation session - used to
 * gate the /impersonate sidebar nav. Super_admin sees it unconditionally;
 * a partner_admin sees it only when at least one partner in their write
 * scope has can_impersonate=true (they'd otherwise land on a page where
 * every target button 403s).
 */
export function canOpenImpersonatePage(
  profile: RoleProfile | undefined,
  visiblePartners: ReadonlyArray<Pick<Partner, "path" | "can_impersonate">>,
): boolean {
  if (isSuperAdmin(profile)) return true;
  return visiblePartners.some((p) => p.can_impersonate && hasWriteOn(profile, p.path));
}

/**
 * True when the caller has ANY partner-tier write grant - a
 * capability check for ``POST /admin/v1/users/{id}/remove-from-hierarchy``.
 *
 * Super_admin also satisfies this predicate, but some UIs
 * intentionally hide the Remove action for super_admin and use
 * Cognito ``/disable`` instead (platform-wide lockout vs. per-
 * subtree membership strip). Treat this helper as a permission
 * check for the API route, not as a guarantee that a Remove button
 * should be rendered in every surface.
 */
export function canRemoveUsersFromHierarchy(profile: RoleProfile | undefined): boolean {
  if (!profile) return false;
  if (isSuperAdmin(profile)) return true;
  return profile.roles.some((r) => WRITE_ROLES.has(r.role));
}
